import React, { useEffect, useState, useCallback } from "react";
import { X, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowUpRight, Truck, Barcode, MapPin } from "@phosphor-icons/react";

/**
 * Full-screen product lightbox. Click the product photo → opens this.
 *
 * Props:
 *  - product: shop product object (or null → closed)
 *  - onClose: () => void
 *  - onOrder: (product) => void        — "Order through us" click
 *  - onBuyDirect: (product) => void    — logged when user clicks Buy on Amazon
 */
export default function ProductLightbox({ product, onClose, onOrder, onBuyDirect }) {
  const [zoomed, setZoomed] = useState(false);

  // Reset zoom whenever a new product opens.
  useEffect(() => { if (product) setZoomed(false); }, [product?.id]);

  // Close on ESC.
  const handleKey = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    if (!product) return;
    document.addEventListener("keydown", handleKey);
    // Prevent body scroll while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [product, handleKey]);

  if (!product) return null;

  const p = product;
  const price = Number(p.list_price ?? p.price ?? 0).toFixed(2);

  return (
    <div
      data-testid="product-lightbox"
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-6 md:p-8 bg-black/85"
      style={{ backdropFilter: "blur(6px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={p.name}
    >
      {/* Close button — floats top-right of the viewport */}
      <button
        type="button"
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close"
        className="fixed top-4 right-4 md:top-6 md:right-6 z-10 inline-flex items-center justify-center w-11 h-11 bg-white/10 hover:bg-white/20 text-white transition-colors rounded-full"
      >
        <X size={20} weight="bold" />
      </button>

      {/* Modal card — stop clicks from bubbling to backdrop */}
      <div
        className="relative w-full max-w-6xl max-h-[92vh] bg-[var(--ji-surface)] overflow-hidden flex flex-col md:flex-row shadow-2xl"
        style={{ borderRadius: 6 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image side */}
        <div className="relative md:w-3/5 bg-[var(--ji-surface-alt)] flex items-center justify-center overflow-hidden group" style={{ minHeight: 320 }}>
          <div
            className="w-full h-full flex items-center justify-center overflow-auto cursor-zoom-in"
            style={{ maxHeight: "60vh", minHeight: 320 }}
            onClick={() => setZoomed((z) => !z)}
          >
            <img
              src={p.image_url}
              alt={p.name}
              data-testid="lightbox-image"
              loading="eager"
              decoding="async"
              onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=1200&q=80"; }}
              className="transition-transform duration-500 ease-out select-none"
              draggable={false}
              style={{
                transform: zoomed ? "scale(1.9)" : "scale(1)",
                cursor: zoomed ? "zoom-out" : "zoom-in",
                maxWidth: "100%",
                maxHeight: "60vh",
                objectFit: "contain",
              }}
            />
          </div>

          {/* Badges */}
          {p.badge && (
            <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 bg-[var(--ji-accent)] text-white font-bold" style={{ borderRadius: 4 }}>
              {p.badge}
            </span>
          )}
          {(p.local_sources && p.local_sources.length > 0) && (
            <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 bg-white/95 text-[var(--ji-primary)] font-bold inline-flex items-center gap-1" style={{ borderRadius: 4 }}>
              <MapPin size={10} weight="fill" /> Local
            </span>
          )}

          {/* Zoom hint */}
          <button
            type="button"
            data-testid="lightbox-zoom-toggle"
            onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
            className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3 h-9 bg-black/60 hover:bg-black/80 text-white text-xs font-semibold transition-colors"
            style={{ borderRadius: 4 }}
            aria-label={zoomed ? "Zoom out" : "Zoom in"}
          >
            {zoomed ? <MagnifyingGlassMinus size={14} weight="bold" /> : <MagnifyingGlassPlus size={14} weight="bold" />}
            {zoomed ? "Zoom out" : "Click to zoom"}
          </button>
        </div>

        {/* Details side */}
        <div className="flex-1 flex flex-col overflow-y-auto md:max-h-[92vh]">
          <div className="p-6 md:p-8 flex-1">
            {p.brand && (
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ji-muted)]">{p.brand}</div>
            )}
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-2 text-2xl md:text-3xl font-bold tracking-tight text-[var(--ji-text)] leading-tight">
              {p.name}
            </h2>

            {p.part_number && (
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-[var(--ji-muted)] font-mono">
                <Barcode size={13} weight="bold" /> Part #{p.part_number}
              </div>
            )}

            {p.compatible_models && p.compatible_models.length > 0 && (
              <div className="mt-4 p-3 bg-[var(--ji-surface-alt)]" style={{ borderRadius: 4 }} data-testid="lightbox-fits">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)] mb-1">Fits</div>
                <div className="text-xs text-[var(--ji-text)] leading-relaxed">
                  {p.compatible_models.join(", ")}
                </div>
              </div>
            )}

            {p.description && (
              <p className="mt-5 text-sm text-[var(--ji-text)] leading-relaxed" data-testid="lightbox-description">
                {p.description}
              </p>
            )}

            <div className="mt-6 pt-5 border-t border-[var(--ji-border)] flex items-baseline justify-between">
              <div>
                <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl font-bold text-[var(--ji-primary)]">
                  ${price}
                </div>
                {p.list_price && Number(p.list_price) !== Number(p.price) && (
                  <div className="text-[11px] text-[var(--ji-muted)] mt-1">Via us · includes service markup</div>
                )}
              </div>
              {p.supplier && (
                <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--ji-muted)]">Ships from {p.supplier}</span>
              )}
            </div>
          </div>

          {/* Sticky action bar */}
          <div className="p-4 md:p-6 border-t border-[var(--ji-border)] bg-[var(--ji-surface)] flex flex-col gap-2">
            <button
              type="button"
              data-testid="lightbox-order"
              onClick={() => { onOrder?.(p); onClose(); }}
              className="ji-btn-accent inline-flex items-center justify-center gap-2 px-4 h-12 text-sm font-semibold w-full"
              style={{ borderRadius: 4 }}
            >
              <Truck size={16} weight="bold" /> Order through us
            </button>
            {p.affiliate_url && (
              <a
                href={p.affiliate_url}
                target="_blank"
                rel="noopener noreferrer sponsored nofollow"
                onClick={() => onBuyDirect?.(p)}
                data-testid="lightbox-buy-direct"
                className="ji-btn-outline inline-flex items-center justify-center gap-2 px-4 h-11 text-xs font-semibold w-full"
                style={{ borderRadius: 4 }}
              >
                Or buy direct on {p.supplier || "Amazon"} <ArrowUpRight size={12} weight="bold" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
